import type {
  CareProgramAPIResponse,
  CareProgram,
  CareProgramDetail,
  EnrollmentStatus,
  WhatToExpectItem,
} from '../types';
import { healthTopicFromAPIString } from './healthTopicUtils';

export const transformCareProgram = (apiProgram: CareProgramAPIResponse): CareProgram => ({
  id: apiProgram.id,
  name: apiProgram.name,
  displayTitle: apiProgram.care_program_mappings.member_facing_name,
  description: apiProgram.description,
  targetAudience: apiProgram.target_audience,
  programLengthDays: apiProgram.program_length_days,
  rolling: apiProgram.rolling,
  tags: apiProgram.tags,
  programType: apiProgram.care_program_type,
  todoTypes: apiProgram.to_do_types,
  averageTaskRate: apiProgram.average_task_rate,
  tableOfContentsUrl: apiProgram.table_of_contents_url,
  mappings: {
    memberFacingName: apiProgram.care_program_mappings.member_facing_name,
    memberFacingDescription: apiProgram.care_program_mappings.member_facing_description,
    healthTopics: apiProgram.care_program_mappings.health_topics,
    healthGoals: apiProgram.care_program_mappings.health_goals,
    isMemberVisible: apiProgram.care_program_mappings.is_member_visible,
    targetPopulation: apiProgram.care_program_mappings.target_population,
    careArea: apiProgram.care_program_mappings.care_area,
    phase: apiProgram.care_program_mappings.phase,
  },
  subscriptions: apiProgram.care_program_subscriptions.map((sub) => ({
    id: sub.id,
    isEnded: sub.is_ended,
    startDate: sub.start_date,
  })),
  requestId: apiProgram.care_program_request_id,
  availableToAll: apiProgram.available_to_all,
});

/**
 * Derives the "What to Expect" items based on program type and metadata.
 * Uses program_length_days and to_do_types to determine which items to include.
 */
const deriveWhatToExpect = (apiProgram: CareProgramAPIResponse): WhatToExpectItem[] => {
  const items: WhatToExpectItem[] = [];

  // Check if program has tracking/check-in features
  const hasCheckIns = apiProgram.to_do_types?.some(
    (type) => type.toLowerCase().includes('check_in') || type.toLowerCase().includes('survey'),
  );

  const isTrackingProgram = apiProgram.care_program_type === 'Tracking Program';
  const isEducationalProgram = apiProgram.care_program_type === 'Educational Program';

  // Tracking programs typically have check-in and view progress
  if (isTrackingProgram) {
    items.push({ type: 'checkInViewProgress' });
  } else if (hasCheckIns) {
    items.push({ type: 'checkIn' });
  }

  // Educational programs have articles
  if (isEducationalProgram) {
    items.push({ type: 'educationalArticles' });
  }

  // Add days of content if program has a defined length
  if (apiProgram.program_length_days && apiProgram.program_length_days > 0) {
    items.push({ type: 'daysOfContent', daysCount: apiProgram.program_length_days });
  }

  return items;
};

/**
 * Transforms API response to CareProgramDetail format for display in detail modal.
 * Maps API fields to CareProgramDetail interface and derives whatToExpect items.
 */
export const transformCareProgramToDetail = (
  apiProgram: CareProgramAPIResponse,
): CareProgramDetail => {
  // Get the primary health topic (use first one if multiple)
  const primaryHealthTopicString = apiProgram.care_program_mappings.health_topics[0];
  const healthTopic = primaryHealthTopicString
    ? healthTopicFromAPIString(primaryHealthTopicString)
    : 'generic';

  const activeSubscription = apiProgram.care_program_subscriptions?.find((sub) => !sub.is_ended);
  let enrollmentStatus: EnrollmentStatus;
  if (activeSubscription) {
    enrollmentStatus = 'enrolled';
  } else if (apiProgram.care_program_request_id) {
    enrollmentStatus = 'requested';
  } else {
    enrollmentStatus = 'not_enrolled';
  }

  return {
    healthTopic,
    title: apiProgram.care_program_mappings.member_facing_name || apiProgram.display_title,
    summary: apiProgram.care_program_mappings.member_facing_description || apiProgram.description,
    whatToExpect: deriveWhatToExpect(apiProgram),
    programId: apiProgram.id,
    enrollmentStatus,
  };
};
